// Formatting and paths for the Investing pages (projects/investing §5).

import { displayName } from "../../tasks/userDisplay";

/** The API's verdicts (InvestingVerdictEnum), in the order a person reads them. */
export const VERDICTS = [
  { value: "unreviewed", label: "Unreviewed" },
  { value: "watch", label: "Watch" },
  { value: "own", label: "Own" },
  { value: "avoid", label: "Avoid" },
];

export function verdictLabel(value) {
  return VERDICTS.find((v) => v.value === value)?.label ?? value;
}

const UNITS = [
  [1e12, "T"],
  [1e9, "B"],
  [1e6, "M"],
];

/**
 * A market cap (a decimal string of dollars, as the API sends it) as
 * "$5.54T", "$312B", "$850M" — three significant figures. Under a million,
 * whole dollars. null when there's no figure: the caller says "unknown",
 * never a made-up number.
 */
export function formatMarketCap(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  for (let i = 0; i < UNITS.length; i += 1) {
    const [size, unit] = UNITS[i];
    if (value < size) continue;
    const rounded = Number((value / size).toPrecision(3));
    // 999.95B rounds to 1000B: say it as the next unit up.
    if (rounded >= 1000 && i > 0) {
      const [bigger, biggerUnit] = UNITS[i - 1];
      return `$${(value / bigger).toPrecision(3)}${biggerUnit}`;
    }
    return `$${(value / size).toPrecision(3)}${unit}`;
  }
  return `$${Math.round(value).toLocaleString("en-US")}`;
}

/** A thesis score (−2..+2) with its sign: "+2", "0", "−1" (a real minus). */
export function formatScore(score) {
  if (score > 0) return `+${score}`;
  if (score < 0) return `−${Math.abs(score)}`;
  return "0";
}

/** A score's colour: moss above 0, brick below, muted at 0. */
export function scoreTone(score) {
  if (score > 0) return "text-success";
  if (score < 0) return "text-destructive";
  return "text-muted-foreground";
}

/** How many scores a list row shows. */
export const TOP_SCORES = 3;

/**
 * The scores a row shows — the first TOP_SCORES, since the API sends them
 * highest first — and how many there are in all, so the row can say
 * "top 3 of 7" rather than pass a few off as all of them.
 */
export function topScores(scores) {
  const all = Array.isArray(scores) ? scores : [];
  return { shown: all.slice(0, TOP_SCORES), total: all.length };
}

/** An instant as a day ("Oct 9, 2026") in `timeZone` (default: the viewer's). */
export function formatDay(iso, timeZone) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * A calendar day ("YYYY-MM-DD" — a record's date, the scoreboard's
 * `through`, the inception) as "Oct 1, 2026": the day itself, in no time
 * zone. null for anything else.
 */
export function formatCalendarDay(day) {
  if (typeof day !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const date = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/**
 * An instant as a day and a time ("Oct 11, 2026, 3:04 PM") in `timeZone`
 * (default: the viewer's) — for readings where the hour matters (a
 * snapshot, Robinhood's approval reading, a plan's expiry).
 */
export function formatInstant(iso, timeZone) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
}

/** A company's page. */
export function companyPath(symbol) {
  return `/investing/research/${encodeURIComponent(symbol)}`;
}

/** A thesis's page. */
export function thesisPath(slug) {
  return `/investing/theses/${encodeURIComponent(slug)}`;
}

/** A board task, opened on the board (TaskBoard's ?task= deep link). */
export function taskBoardPath(id) {
  return `/tasks/board?task=${id}`;
}

/**
 * Who did something: their name ("unnamed account #3" when it renders
 * blank, #38), or a plain word when they're gone (SET_NULL).
 */
export function whoName(user) {
  return user ? displayName(user) : "someone no longer here";
}

/** A research note's anchor on its company's page: /investing/research/NVDA#note-12. */
export function noteAnchor(id) {
  return `note-${id}`;
}

/** A discussion's page. */
export function discussionPath(id) {
  return `/discussions/${id}`;
}
